var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import { Box } from "@mui/material";
import { BODY_TEXT_COLOR, LEGEND_TEXT_COLOR, PAGE_SIZE } from "../utils/chartConstants";
import { formatNumericValue, formatPercentValue, getChartDataset, parseNumericValue, resolveLabelAndValueKeys, } from "../../../utils/chartData";
import { normalizeChartDisplayData, normalizeDisplayText, } from "../../../utils/displayText";
export default function TableRenderer(_a) {
    var _b;
    var data = _a.data, title = _a.title, bare = _a.bare, tablePage = _a.tablePage, onPageChange = _a.onPageChange;
    try {
        var displayData = normalizeChartDisplayData(data);
        var dataset_1 = getChartDataset(displayData);
        var declaredColumns_1 = ((_b = displayData.columns) !== null && _b !== void 0 ? _b : []).filter(function (column) {
            return dataset_1.some(function (item) { return item[column.name] !== undefined; });
        });
        var useExplicitColumns = declaredColumns_1.length > 2;
        var _c = resolveLabelAndValueKeys(displayData, dataset_1), labelKey_1 = _c.labelKey, valueKey_1 = _c.valueKey;
        var labels_1 = dataset_1.map(function (item) { var _a; return String((_a = item[labelKey_1]) !== null && _a !== void 0 ? _a : ""); });
        var values_1 = dataset_1.map(function (item) { var _a; return parseNumericValue((_a = item[valueKey_1]) !== null && _a !== void 0 ? _a : item.value); });
        var total_1 = values_1.reduce(function (acc, v) { return acc + v; }, 0);
        var totalPages_1 = Math.ceil(dataset_1.length / PAGE_SIZE);
        if (totalPages_1 <= 1 && tablePage !== 0) {
            onPageChange(0);
        }
        var pageStart = tablePage * PAGE_SIZE;
        var pageEnd = pageStart + PAGE_SIZE;
        var pageIndexes = dataset_1.map(function (_, i) { return i; }).slice(pageStart, pageEnd);
        var colHeader = labelKey_1
            .replace(/_/g, " ")
            .replace(/\b\w/g, function (c) { return c.toUpperCase(); });
        var explicitHeaderMap_1 = {
            ocupacao: "Ocupação",
            frequencia_absoluta: "Freq. Absoluta",
            frequencia_acumulada: "Freq. Acumulada",
            frequencia_relativa: "Freq. Relativa",
        };
        var normalizeColumnName_1 = function (name) {
            return name.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
        };
        var formatExplicitHeader_1 = function (name) {
            return explicitHeaderMap_1[name] ||
                name.replace(/_/g, " ").replace(/\b\w/g, function (c) { return c.toUpperCase(); });
        };
        var isPercentualColumn_1 = function (name) {
            return name === "percentual_vitimas" || name === "percentual_violacoes";
        };
        var normalizedColumnNames = declaredColumns_1.map(function (column) {
            return normalizeColumnName_1(column.name);
        });
        var isCorPeleFaixaEtariaMatrix_1 = useExplicitColumns &&
            normalizedColumnNames.length >= 6 &&
            normalizedColumnNames[0] === "cor_da_pele" &&
            normalizedColumnNames
                .slice(1)
                .every(function (name) { return /^\d+_a_\d+$/.test(name) || name === "70_ou_mais"; });
        var explicitColumnWidths = isCorPeleFaixaEtariaMatrix_1
            ? declaredColumns_1.map(function (_, index) {
                return index === 0
                    ? "22%"
                    : "".concat(78 / Math.max(declaredColumns_1.length - 1, 1), "%");
            })
            : null;
        var parsePercentString_1 = function (rawValue) {
            if (typeof rawValue !== "string" || !rawValue.includes("%"))
                return null;
            var parsed = Number(rawValue.replace("%", "").replace(",", "."));
            return Number.isFinite(parsed) ? parsed : null;
        };
        var formatExplicitValue_1 = function (name, type, rawValue) {
            if (type !== "numeric")
                return normalizeDisplayText(rawValue !== null && rawValue !== void 0 ? rawValue : "—");
            var percentValue = parsePercentString_1(rawValue);
            if (percentValue !== null)
                return formatPercentValue(percentValue);
            var parsed = parseNumericValue(rawValue);
            if (!Number.isFinite(parsed))
                return "—";
            if (name === "frequencia_relativa" || name === "frequencia_acumulada") {
                return formatNumericValue(parsed, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                });
            }
            return formatNumericValue(parsed);
        };
        var tableEl = (_jsxs(Box, { children: [_jsx(Box, __assign({ sx: { overflowX: "auto" } }, { children: _jsx("table", __assign({ style: {
                            width: "100%",
                            borderCollapse: "collapse",
                            fontFamily: "Montserrat, sans-serif",
                            fontSize: "0.82rem",
                            tableLayout: isCorPeleFaixaEtariaMatrix_1 ? "fixed" : "auto",
                        } }, { children: useExplicitColumns ? (_jsxs(_Fragment, { children: [explicitColumnWidths && (_jsx("colgroup", { children: explicitColumnWidths.map(function (width, index) { return (_jsx("col", { style: { width: width } }, "col-".concat(index))); }) })), _jsx("thead", { children: _jsx("tr", { children: declaredColumns_1.map(function (column, index) { return (_jsx("th", __assign({ style: {
                                                background: "#034EA2",
                                                color: "#fff",
                                                fontWeight: 700,
                                                padding: "10px 14px",
                                                textAlign: column.type === "numeric" ? "right" : "left",
                                                whiteSpace: isCorPeleFaixaEtariaMatrix_1 && index === 0
                                                    ? "nowrap"
                                                    : "normal",
                                            } }, { children: formatExplicitHeader_1(column.name) }), column.name)); }) }) }), _jsx("tbody", { children: pageIndexes.map(function (i) { return (_jsx("tr", __assign({ style: {
                                            borderBottom: "1px solid #F3F4F6",
                                            background: i % 2 === 0 ? "#fff" : "#FAFAFA",
                                            color: BODY_TEXT_COLOR,
                                        } }, { children: declaredColumns_1.map(function (column) {
                                            var _a, _b;
                                            return (_jsx("td", __assign({ style: {
                                                    padding: "8px 14px",
                                                    textAlign: column.type === "numeric" ? "right" : "left",
                                                    color: isPercentualColumn_1(column.name)
                                                        ? "#2563EB"
                                                        : BODY_TEXT_COLOR,
                                                    whiteSpace: isCorPeleFaixaEtariaMatrix_1 &&
                                                        column.name === ((_a = declaredColumns_1[0]) === null || _a === void 0 ? void 0 : _a.name)
                                                        ? "nowrap"
                                                        : "normal",
                                                } }, { children: formatExplicitValue_1(column.name, column.type, (_b = dataset_1[i]) === null || _b === void 0 ? void 0 : _b[column.name]) }), "".concat(i, "-").concat(column.name)));
                                        }) }), i)); }) }), _jsx("tfoot", { children: _jsx("tr", __assign({ style: {
                                            borderTop: "2px solid #E5E7EB",
                                            fontWeight: 700,
                                            color: BODY_TEXT_COLOR,
                                        } }, { children: declaredColumns_1.map(function (column, index) { return (_jsx("td", __assign({ style: {
                                                padding: "8px 14px",
                                                textAlign: column.type === "numeric" ? "right" : "left",
                                                color: isPercentualColumn_1(column.name)
                                                    ? "#2563EB"
                                                    : BODY_TEXT_COLOR,
                                            } }, { children: index === 0
                                                ? "Total"
                                                : column.type !== "numeric"
                                                    ? "—"
                                                    : isPercentualColumn_1(column.name)
                                                        ? formatPercentValue(100)
                                                        : column.name === "frequencia_acumulada"
                                                            ? formatExplicitValue_1(column.name, column.type, Math.max.apply(Math, dataset_1.map(function (item) {
                                                                var _a;
                                                                return (_a = parsePercentString_1(item[column.name])) !== null && _a !== void 0 ? _a : parseNumericValue(item[column.name]);
                                                            })))
                                                            : formatExplicitValue_1(column.name, column.type, dataset_1.reduce(function (acc, item) {
                                                                var _a;
                                                                return acc +
                                                                    ((_a = parsePercentString_1(item[column.name])) !== null && _a !== void 0 ? _a : parseNumericValue(item[column.name]));
                                                            }, 0)) }), "total-".concat(column.name))); }) })) })] })) : (_jsxs(_Fragment, { children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", __assign({ style: {
                                                    background: "#034EA2",
                                                    color: "#fff",
                                                    fontWeight: 700,
                                                    padding: "10px 14px",
                                                    textAlign: "left",
                                                } }, { children: colHeader })), _jsx("th", __assign({ style: {
                                                    background: "#034EA2",
                                                    color: "#fff",
                                                    fontWeight: 700,
                                                    padding: "10px 14px",
                                                    textAlign: "right",
                                                } }, { children: "No. Vitimas" })), _jsx("th", __assign({ style: {
                                                    background: "#034EA2",
                                                    color: "#fff",
                                                    fontWeight: 700,
                                                    padding: "10px 14px",
                                                    textAlign: "right",
                                                } }, { children: "% do Total" }))] }) }), _jsx("tbody", { children: pageIndexes.map(function (i) { return (_jsxs("tr", __assign({ style: {
                                            borderBottom: "1px solid #F3F4F6",
                                            background: i % 2 === 0 ? "#fff" : "#FAFAFA",
                                            color: BODY_TEXT_COLOR,
                                        } }, { children: [_jsx("td", __assign({ style: {
                                                    padding: "8px 14px",
                                                    color: BODY_TEXT_COLOR,
                                                } }, { children: labels_1[i] })), _jsx("td", __assign({ style: {
                                                    padding: "8px 14px",
                                                    textAlign: "right",
                                                    color: BODY_TEXT_COLOR,
                                                } }, { children: formatNumericValue(values_1[i]) })), _jsx("td", __assign({ style: {
                                                    padding: "8px 14px",
                                                    textAlign: "right",
                                                    color: "#1D4ED8",
                                                    fontWeight: 500,
                                                } }, { children: total_1 > 0
                                                    ? formatPercentValue((values_1[i] / total_1) * 100)
                                                    : "—" }))] }), i)); }) }), _jsx("tfoot", { children: _jsxs("tr", __assign({ style: {
                                            borderTop: "2px solid #E5E7EB",
                                            fontWeight: 700,
                                            color: BODY_TEXT_COLOR,
                                        } }, { children: [_jsx("td", __assign({ style: { padding: "8px 14px" } }, { children: "Total" })), _jsx("td", __assign({ style: {
                                                    padding: "8px 14px",
                                                    textAlign: "right",
                                                    color: BODY_TEXT_COLOR,
                                                } }, { children: formatNumericValue(total_1) })), _jsx("td", __assign({ style: {
                                                    padding: "8px 14px",
                                                    textAlign: "right",
                                                    color: BODY_TEXT_COLOR,
                                                } }, { children: formatPercentValue(100) }))] })) })] })) })) })), totalPages_1 > 1 && (_jsxs(Box, __assign({ sx: {
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        gap: 1,
                        mt: 1.5,
                        fontFamily: "Montserrat, sans-serif",
                        fontSize: "0.78rem",
                        color: LEGEND_TEXT_COLOR,
                    } }, { children: [_jsx(Box, __assign({ component: "button", onClick: function () { return onPageChange(Math.max(0, tablePage - 1)); }, disabled: tablePage === 0, sx: {
                                px: 1.5,
                                py: 0.5,
                                borderRadius: "6px",
                                border: "1px solid #E5E7EB",
                                background: tablePage === 0 ? "#F9FAFB" : "#fff",
                                color: tablePage === 0 ? "#D1D5DB" : "#034EA2",
                                fontFamily: "Montserrat, sans-serif",
                                fontSize: "0.78rem",
                                cursor: tablePage === 0 ? "default" : "pointer",
                                fontWeight: 600,
                            } }, { children: "\u2039 Anterior" })), _jsxs(Box, { children: [tablePage + 1, " / ", totalPages_1] }), _jsx(Box, __assign({ component: "button", onClick: function () {
                                return onPageChange(Math.min(totalPages_1 - 1, tablePage + 1));
                            }, disabled: tablePage === totalPages_1 - 1, sx: {
                                px: 1.5,
                                py: 0.5,
                                borderRadius: "6px",
                                border: "1px solid #E5E7EB",
                                background: tablePage === totalPages_1 - 1 ? "#F9FAFB" : "#fff",
                                color: tablePage === totalPages_1 - 1 ? "#D1D5DB" : "#034EA2",
                                fontFamily: "Montserrat, sans-serif",
                                fontSize: "0.78rem",
                                cursor: tablePage === totalPages_1 - 1 ? "default" : "pointer",
                                fontWeight: 600,
                            } }, { children: "Pr\u00F3ximo \u203A" }))] })))] }));
        if (bare)
            return _jsx(_Fragment, { children: tableEl });
        return (_jsxs(Box, __assign({ className: "chat-chart-export", sx: {
                width: "100%",
                background: "#fff",
                borderRadius: "16px",
                border: "1px solid #ECEEF4",
                p: 2,
                mt: 1,
            } }, { children: [title && (_jsx(Box, __assign({ sx: { mb: 1.5, px: 0.5 } }, { children: _jsx(Box, __assign({ component: "div", sx: {
                            fontFamily: "Montserrat, sans-serif",
                            fontWeight: 600,
                            fontSize: "0.9rem",
                            color: "#1F2937",
                        } }, { children: title })) }))), tableEl] })));
    }
    catch (e) {
        console.error("[ChartRenderer] table build failed:", e, data);
        return null;
    }
}
